import {useForm} from 'react-hook-form';
import {TextFieldElement} from 'react-hook-form-mui';
import {yupResolver} from '@hookform/resolvers/yup';
import {Button, DialogActions, DialogContent} from '@mui/material';
import {isAxiosError} from 'axios';
import {useSnackbar} from 'notistack';
import {number, object, string} from 'yup';

import {
  OnboardingExtDataConfig,
  OnboardingType,
  useAddOnboardingExtDataConfig,
  useUpdateOnboardingExtDataConfig,
} from '@entities/onboarding';
import {getErrorMessageFromApiError} from '@shared/api';
import {Dialog, DialogTitle} from '@shared/components';

interface FormValues {
  extName: string;
  extValue: string;
  config: string;
  priority: number;
}

const schema = object().shape({
  extName: string().trim().required(),
  extValue: string().trim().required(),
  config: string().trim().required(),
  priority: number().typeError('Enter an integer').integer().required(),
});

interface Props {
  type: OnboardingType;
  config?: OnboardingExtDataConfig | null;
  onClose: () => void;
}

export const UtmConfigDialog = ({type, config, onClose}: Props) => {
  const {enqueueSnackbar} = useSnackbar();
  const {mutateAsync: addConfig, isPending: isAdding} =
    useAddOnboardingExtDataConfig();
  const {mutateAsync: updateConfig, isPending: isUpdating} =
    useUpdateOnboardingExtDataConfig();
  const isSaving = isAdding || isUpdating;
  const {control, handleSubmit} = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      extName: config?.extName ?? '',
      extValue: config?.extValue ?? '',
      config: config?.config ?? '',
      priority: config?.priority ?? 0,
    },
  });

  const handleClose = () => {
    if (!isSaving) onClose();
  };

  const onSubmit = async (values: FormValues) => {
    if (isSaving) return;

    try {
      await (config
        ? updateConfig({
            type,
            configId: config.id,
            config: values.config,
            priority: values.priority,
          })
        : addConfig({type, ...values}));
      enqueueSnackbar(
        `UTM config ${config ? 'updated' : 'added'} successfully`,
        {
          variant: 'success',
        },
      );
      onClose();
    } catch (error) {
      const message = isAxiosError(error)
        ? getErrorMessageFromApiError(error)
        : '';
      enqueueSnackbar(`Could not save UTM config. ${message}`, {
        variant: 'error',
      });
    }
  };

  return (
    <Dialog
      open
      onClose={handleClose}
      scroll="body"
      slotProps={{
        paper: {
          sx: {
            p: 3,
            height: 'auto',
            maxHeight: 'none',
          },
        },
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogTitle
          title={config ? 'Edit UTM config' : 'Add UTM config'}
          sx={{px: 3, pt: 3}}
        />
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            minWidth: 380,
            px: 3,
            '&&': {pt: 4},
            overflow: 'visible',
            flex: '0 0 auto',
            '& .MuiInputBase-root.Mui-disabled': {
              bgcolor: 'action.disabledBackground',
              '& fieldset': {backgroundColor: 'transparent'},
            },
            '& .MuiFormHelperText-root': {
              position: 'static',
              transform: 'none',
              mt: 1,
            },
            '& .MuiFormHelperText-root:not(.Mui-error)': {
              color: 'text.secondary !important',
            },
          }}
        >
          <TextFieldElement
            name="extName"
            label="UTM parameter (e.g. utm_source)"
            control={control}
            fullWidth
            disabled={!!config || isSaving}
          />
          <TextFieldElement
            name="extValue"
            label="UTM value (e.g. tiktok)"
            control={control}
            fullWidth
            disabled={!!config || isSaving}
          />
          <TextFieldElement
            name="config"
            label="Config URL"
            control={control}
            fullWidth
            disabled={isSaving}
          />
          <TextFieldElement
            name="priority"
            label="Priority"
            type="number"
            control={control}
            fullWidth
            disabled={isSaving}
          />
        </DialogContent>
        <DialogActions sx={{px: 3, pb: 3}}>
          <Button variant="outlined" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSaving}>
            {config ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
